import React, { useEffect, useRef } from "react";
import Lenis from "lenis";
import { motion, useScroll, useTransform, useInView } from "framer-motion";

/* ============================================================
 *  Lenis smooth-scroll provider
 * ============================================================ */
export function SmoothScroll({ children }) {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.35,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 1.4,
    });
    let raf;
    function loop(time) {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    }
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);
  return children;
}

/* ============================================================
 *  Masked line reveal — used for hero + section headings
 *  Wraps each child (usually a word/line) in an overflow:hidden
 *  mask and slides them up on load or in view.
 * ============================================================ */
const revealVariants = {
  hidden: { y: "115%", opacity: 0 },
  show: (i = 0) => ({
    y: "0%",
    opacity: 1,
    transition: {
      duration: 1.1,
      delay: 0.05 + i * 0.08,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

export function LineReveal({
  children,
  as: Tag = "span",
  delay = 0,
  className = "",
  inView = false,
}) {
  // children can be a string (auto-split by words) or JSX
  const words =
    typeof children === "string" ? children.split(" ") : [children];
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-15% 0px" });
  const shouldAnimate = inView ? isInView : true;

  return (
    <Tag ref={ref} className={className}>
      {words.map((word, i) => (
        <span key={i} className="mask">
          <motion.span
            className="inline-block"
            variants={revealVariants}
            custom={i + delay * 10}
            initial="hidden"
            animate={shouldAnimate ? "show" : "hidden"}
          >
            {word}
            {i < words.length - 1 && "\u00A0"}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
}

/* ============================================================
 *  Section scroll-reveal (subtle fade+lift when entering view)
 * ============================================================ */
export function ScrollReveal({
  children,
  className = "",
  y = 28,
  delay = 0,
  duration = 0.9,
  once = true,
  ...rest
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once, margin: "-10% 0px" });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      transition={{ duration, delay, ease: [0.22, 1, 0.36, 1] }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

/* ============================================================
 *  Parallax image container — subtle Y shift with scroll
 * ============================================================ */
export function Parallax({ children, offset = 60, className = "" }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [offset, -offset]);
  return (
    <div ref={ref} className={className}>
      <motion.div style={{ y }} className="will-change-transform">
        {children}
      </motion.div>
    </div>
  );
}

/* ============================================================
 *  Editorial Marquee — huge slow scrolling type
 * ============================================================ */
export function EditorialMarquee({ items = [], speed = 55, className = "" }) {
  return (
    <div className={`relative overflow-hidden ${className}`}>
      <div
        className="flex whitespace-nowrap marquee-x"
        style={{ animationDuration: `${speed}s` }}
      >
        {[...items, ...items, ...items, ...items].map((item, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-6 px-6 shrink-0"
          >
            <span className="font-editorial italic font-light text-[10vw] leading-none text-[#EEEEEF] tracking-tighter">
              {item.label}
            </span>
            <span className="w-3 h-3 rounded-full bg-[#0E93EC] shrink-0" />
          </span>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
 *  Magnetic Button — subtle hover magnetism
 * ============================================================ */
export function Magnetic({ children, strength = 12, className = "" }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const relX = e.clientX - rect.left - rect.width / 2;
      const relY = e.clientY - rect.top - rect.height / 2;
      el.style.transform = `translate(${(relX / rect.width) * strength}px, ${
        (relY / rect.height) * strength
      }px)`;
    };
    const onLeave = () => {
      el.style.transform = "translate(0,0)";
    };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, [strength]);
  return (
    <div
      ref={ref}
      className={className}
      style={{ transition: "transform 0.35s cubic-bezier(0.22,1,0.36,1)" }}
    >
      {children}
    </div>
  );
}
